import router from '@/router';
import { menu } from '@/.';
import type { MenuItem } from '@/objects';
import { useEventStoreRef, useToastStore, useUtilStore } from '@/stores';
import { useHelper } from '@/composeables';
import { defineStore } from 'pinia';

interface KeyBoardTrigger {
    on: string;
    action: (event?: KeyboardEvent) => void;
    bubble?: boolean;
    alias?: string;
}

/**
 * Handler function for when we try and change tabs with CTRL+ALT+Number
 * @param tabid
 */
function handleTabEvents(tabid: number) {
    // Find the visible modal
    const modal = document.querySelector('.modal.show');

    let tabs = null;

    // Get the list of tabs
    if (modal) {
        tabs = modal.querySelectorAll('.nav.nav-tabs .nav-item');
    } else {
        tabs = document.querySelectorAll('.eq-viewcontent .nav.nav-tabs .nav-item');
    }

    // If we have tabs and the tabid is less than the number of tabs click the tab
    if (tabs?.length >= tabid) {
        tabs[tabid - 1]!.querySelector('a')?.click();
    }
}

/**
 * We try and handle the global CTRL+ENTER event.
 * This is used to try and press the closes button that is marked as .btn-submi
 * @param event
 */
const handleCtrlEnterEvent = (event: KeyboardEvent) => {
    if (!event || !event.target) return;

    const srcElement = event.target as HTMLElement;

    // are we in a modal?
    const modal = srcElement.closest('.modal-content') as HTMLElement;

    if (modal) {
        // find the primary button
        const primaryButton = modal.querySelector('.btn-submit:not([disabled])') as HTMLButtonElement;

        if (primaryButton) {
            primaryButton.click();
        }
    }
};

/**
 * The store for the keyboard listener
 */
export const useKeyboardListener = defineStore('keyboardlistener', () => {
    // A list of the reigstered shortcut triggers
    let triggers = new Array<KeyBoardTrigger>();

    const globalTriggers = new Array<KeyBoardTrigger>();

    /// Is the keyboard listener active
    let active = false;

    /**
     * This will stop the keyboard event to reach the browser
     * @param ev
     * @returns
     */
    const stop = (ev: KeyboardEvent) => {
        ev.preventDefault();
        ev.stopPropagation();
        return false;
    };

    const keydown = (ev: KeyboardEvent) => {
        // this is not neccessarily a keyboard event, autofill will trigger without key property
        if (!active) return;

        if (!ev || !ev.key) {
            return;
        }

        if (checkTriggers(ev)) {
            console.log('Preventing default action for key:', ev.key);
            return stop(ev);
        }
    };

    /**
     * Add a trigger to the list of registered triggers
     * @param trigger
     */
    const addTrigger = (trigger: KeyBoardTrigger) => {
        trigger.on = trigger.on.toUpperCase();
        // Check if trigger allready exists in globalTriggers
        if (triggers.some((t) => t.on == trigger.on && t.alias == 'Global')) {
            return;
        }

        console.debug('Adding keyboard shortcut:', trigger.on);
        triggers.push(trigger);
    };

    /**
     * Remove a trigger by alias
     * @param alias
     */
    const removeTriggerByAlias = (alias: string) => {
        // Remove all triggers with the alias
        triggers = triggers.filter((t) => t.alias != alias);
    };

    /**
     * Check the triggers that is registered for this component and fires it and return true
     * @param event
     * @returns
     */
    const checkTriggers = (event: KeyboardEvent) => {
        let isFound = false;
        const _state = new Set<string>();

        const key = event.key == ' ' ? 'SPACE' : event.key.toUpperCase();

        // ignore shift, alt and ctrl keys
        if (key == 'CONTROL' || key == 'ALT' || key == 'SHIFT') {
            return false;
        }

        if (event.ctrlKey) {
            _state.add('CTRL');
        }
        if (event.altKey) {
            _state.add('ALT');
        }
        if (event.shiftKey) {
            _state.add('SHIFT');
        }

        // We need to handle the + a little different since we use it as a separator
        if (event.key == '+') {
            _state.add('PLUS');
        } else {
            _state.add(event.key == ' ' ? 'SPACE' : event.key.toUpperCase());
        }

        // We always want the last trigger in the list so we need to iterate the list from the end
        for (let i = triggers.length - 1; i >= 0; i--) {
            const trigger = triggers[i];
            const keys = trigger.on.split('+');

            // we need them to be same size
            if (
                keys.length != _state.size ||
                (_state.size == 2 && _state.has('CTRL') && _state.has('F5')) ||
                (_state.size == 1 && _state.has('CTRL'))
            ) {
                continue;
            }

            // and contain all
            let matchAllKeys = true;
            for (const statekey of _state) {
                if (!keys.includes(statekey)) {
                    matchAllKeys = false;
                }
            }

            if (matchAllKeys) {
                console.debug('Keyboardshortcut triggered:', trigger.on);

                // run the action
                trigger.action(event);

                isFound = trigger.bubble === true ? false : true;

                // Make sure we only trigger one event, the last one in the list
                // This mean that if we want to temporary override a global shortcut we need to add it last and then make sure to remove it in the component/view
                break;
            }
        }

        return isFound;
    };

    /**
     * Adds the global event listeners for the keyboard
     */
    const addEventListeners = () => {
        if (active) return;

        window.addEventListener('keydown', keydown);
        active = true;
    };

    /**
     * Helper function to register a menu item with a shortcut key
     * @param item
     * @returns
     */
    const registerMenuShortcutKey = (item: MenuItem) => {
        if (!item.shortcutKey) return;

        globalTriggers.push({
            alias: 'Global',
            on: item.shortcutKey,
            action: () => {
                if (item.eventNeeded) {
                    const { eventID } = useEventStoreRef();
                    if (eventID.value == 0) {
                        useToastStore().addWarning('general.select_event');
                    } else {
                        router.push(`/${eventID.value}${item.route}`);
                    }
                } else {
                    router.push(item.route);
                }
            },
        });
    };

    /**
     * Register all the menu items with shortcut keys
     */
    const registerGlobalTriggers = () => {
        // Register all the menu items with shortcut keys
        menu.forEach((m) => {
            if (m.subItems.length > 0) {
                m.subItems.forEach((sm) => registerMenuShortcutKey(sm));
            } else {
                registerMenuShortcutKey(m);
            }
        });

        // Register all the globale events for CTRL+ALT+Number to switch between tabs
        for (let i = 1; i <= 9; i++) {
            globalTriggers.push({
                alias: 'Global',
                on: `CTRL+ALT+${i}`,
                action: () => handleTabEvents(i),
            });
        }

        // Focus to the first input field
        globalTriggers.push({
            alias: 'Global',
            on: 'CTRL+SPACE',
            action: () => useHelper().setFocus(),
        });

        globalTriggers.push({
            alias: 'Global',
            on: 'CTRL+ALT+SPACE',
            action: () => useHelper().setFocus(true),
        });

        globalTriggers.push({
            alias: 'Global',
            on: 'CTRL+F1',
            action: () => {
                console.log('We should show a modal containing the help page here.');
            },
        });

        globalTriggers.push({
            alias: 'Global',
            on: 'CTRL+ENTER',
            action: (event) => handleCtrlEnterEvent(event!),
        });

        globalTriggers.forEach((trigger) => addTrigger(trigger));
    };

    registerGlobalTriggers();

    addEventListeners();

    // /**
    //  * Triggered every time a KeepAlive view is activated
    //  */
    // onActivated(() => addEventListeners());

    // /**
    //  * Triggered every time a KeepAlive view is deactivated
    //  */
    // onDeactivated(() => removeEventListeners());

    // /**
    //  * Triggered when the component is mounted
    //  */
    // onMounted(() => addEventListeners());

    // /**
    //  * Triggered when the component is unmounted
    //  */
    // onUnmounted(() => removeEventListeners());

    return {
        addKeyboardShortcut: addTrigger,
        removeKeyboardShortcut: removeTriggerByAlias,
        registerGlobalShortcutKeys: registerGlobalTriggers,
    };
});
